import type { SummaryBooked, SummaryBookedDay } from "@planerius/contracts/cabinet"

import { formatDateLong, formatDayMonth } from "../../lib/format"
import { countOf, plural } from "../../lib/plural"
import { formatMoney } from "../directories/format-money"

/**
 * 5-В4 «Записано» как данные, отдельно от разметки.
 *
 * СЛОВА «ПРОГНОЗ» ЗДЕСЬ НЕТ НИ В ОДНОЙ СТРОКЕ, и это требование ТЗ, а не вкус.
 * Прогноз — обещание, построенное на модели; здесь складываются цены записей, которые
 * УЖЕ созданы. Разница видна в первом же разговоре: за прогноз владелец спрашивает
 * с нас, за «записано» — с администратора.
 *
 * ДВЕ ЦИФРЫ РАЗВЕДЕНЫ ФОРМОЙ И ГЛАГОЛОМ. «Записано · предстоит» — деньги, которые
 * ещё могут не случиться; «Получено · состоялось» — те, что уже есть. Сложить их
 * в одну сумму значит показать салону деньги, которых у него нет.
 */

export const BOOKED_PERIODS = [3, 7, 14] as const
export type BookedPeriod = (typeof BOOKED_PERIODS)[number]

export function periodLabel(days: number): string {
  return countOf(days, "день", "дня", "дней")
}

/** «Записано · предстоит» — подпись под суммой. */
export function bookedCaption(booked: SummaryBooked): string {
  const parts = [`на ${periodLabel(booked.days)}`]
  if (booked.bookedCount !== null) {
    parts.push(countOf(booked.bookedCount, "запись", "записи", "записей"))
  }
  parts.push("подтверждены и ждут")
  return parts.join(" · ")
}

/** «Получено · состоялось» — подпись под суммой. */
export function receivedCaption(booked: SummaryBooked): string {
  const parts = ["сегодня"]
  if (booked.receivedCount !== null) {
    parts.push(`${countOf(booked.receivedCount, "визит", "визита", "визитов")} завершено`)
  }
  // «Меняется только назад» — это и есть разница между двумя цифрами экрана.
  parts.push("меняется только назад")
  return parts.join(" · ")
}

/** Сноска под «Записано» — дословно из макета 5-В4. */
export const BOOKED_FOOTNOTE =
  "По ценам уже созданных записей. Отмены и неявки эту сумму уменьшат; новые записи — увеличат."

/**
 * Сравнение с прошлой неделей — с ЧЕСТНОЙ подписью про приблизительность.
 *
 * Истории статусов и переносов в базе нет, поэтому «что было записано неделю назад»
 * восстанавливается по времени создания и отмены записи. Показать такое число без
 * оговорки значит дать владельцу цифру, на которой он построит разговор с
 * администратором, — и окажется неправ.
 *
 * null — сравнивать не с чем: неделю назад записей не было вовсе.
 */
export function comparisonText(booked: SummaryBooked): string | null {
  const { currentMinor, previousMinor, deltaPercent } = booked.comparison
  if (previousMinor === null || previousMinor === 0 || currentMinor === null) return null
  const before = formatMoney(previousMinor) ?? "—"
  const now = formatMoney(currentMinor) ?? "—"
  const direction =
    deltaPercent === null || deltaPercent === 0
      ? "столько же"
      : deltaPercent > 0
        ? `на ${String(deltaPercent)} % больше`
        : `на ${String(Math.abs(deltaPercent))} % меньше`
  // ОБА ЧИСЛА НАЗВАНЫ, И НАЗВАНА МЕРКА. Сравнение идёт не с суммой из карточки:
  // там только предстоящее, а прошлая неделя вся уже отработана. Обе стороны здесь
  // считаются одинаково — «что стоит в календаре», — иначе к вечеру экран показывал бы
  // падение в день, когда не отменили ни одной записи.
  return `В календаре на этот срок стоит ${now}, неделю назад стояло ${before} — ${direction}. Считается всё неотменённое, включая уже отработанное. Счёт приблизительный: база помнит, когда запись создали и когда отменили, но не помнит переносов.`
}

/** Что сказать про горизонт записи, если период уходит за него. */
export function horizonText(booked: SummaryBooked): string | null {
  if (booked.horizonLastDate === null || booked.horizonDays === null) return null
  if (!booked.rows.some((row) => row.beyondHorizon)) return null
  const last = formatDayMonth(booked.horizonLastDate, booked.timezone)
  // ГОРИЗОНТ ОГРАНИЧИВАЕТ ВИТРИНУ, А НЕ САЛОН. Администратор записывает вручную
  // на любую дату, и записи за горизонтом бывают. Говорить «записей нет» про такой
  // день нельзя — это ложь; сказать надо ровно то, что верно: сам клиент туда
  // записаться не мог.
  return `Онлайн-запись не открыта дальше, чем ${last}: горизонт — ${periodLabel(booked.horizonDays)}. Клиент сам на эти дни не запишется, администратор — может.`
}

/**
 * Пустой период объясняется словами, а не цифрой «0,00 ₽» (ТЗ §5.10.1).
 *
 * Ноль рублей владелец читает как провал продаж и звонит администратору. Причин
 * у пустого периода три, и все они не про продажи: смен нет, горизонт закрыт,
 * салон только начал.
 */
export function emptyText(booked: SummaryBooked): string | null {
  if ((booked.bookedCount ?? 0) > 0) return null
  const noShifts = booked.rows.every((row) => row.workMinutes === 0)
  const beyond = booked.rows.filter((row) => row.beyondHorizon).length
  if (beyond === booked.rows.length) {
    return "Весь этот период за горизонтом онлайн-записи — сам клиент сюда записаться не мог. Откройте горизонт шире в настройках филиала или возьмите период короче."
  }
  if (noShifts) {
    return "На эти дни не поставлено ни одной смены. Пока смен нет, записывать некого и некуда: сначала график, потом записи."
  }
  if (beyond > 0) {
    // «Горизонт открыт» про период, у которого закрыта половина, — прямая неправда.
    return `На этот период пока никто не записан. Смены стоят, но ${countOf(beyond, "день", "дня", "дней")} из ${String(booked.rows.length)} закрыт для онлайн-записи — клиент туда не попадёт.`
  }
  return "На этот период пока никто не записан. Смены стоят, горизонт открыт — значит, время есть и его можно продать."
}

/** Строка дня: «Ср, 23 сентября · 2 записи · 62 %». */
export function dayRowCaption(row: SummaryBookedDay): string {
  const parts = [countOf(row.count, "запись", "записи", "записей")]
  if (row.workMinutes > 0) {
    parts.push(`${String(row.percent)} % смен занято`)
  } else {
    // «0 %» на дне без смен обвиняет мастеров в простое, которого не было.
    parts.push("смен нет")
  }
  if (row.beyondHorizon) {
    // Не «записей нет»: администратор записывает на такой день вручную, и записи
    // там бывают. Закрыта витрина, а не день.
    parts.push("онлайн-запись закрыта")
  }
  return parts.join(" · ")
}

/**
 * Заголовок дня в списке: «Ср, 23 сентября».
 *
 * ДЕНЬ НЕДЕЛИ ОБЯЗАТЕЛЕН. Четырнадцать строк подряд «17 сентября, 18 сентября»
 * читаются как таблица чисел; владелец же ищет глазами субботу — у салона суббота
 * и среда живут по-разному, и решение «кого догрузить» принимается по дню недели.
 */
export function dayRowTitle(row: SummaryBookedDay, timezone: string): string {
  return formatDateLong(row.date, timezone)
}

export function dayRowMoney(row: SummaryBookedDay): string {
  return row.count === 0 ? "—" : (formatMoney(row.minor) ?? "—")
}

/** Деградация: отчёты выключены ради записи клиентов (дословно из макета). */
export const BOOKED_DEGRADED =
  "Сумма записанного сейчас не считается: система бережёт ресурсы для записи клиентов. Журнал и запись работают."

/**
 * Заполненность смен за период.
 *
 * null — смен нет вовсе, и «0 %» здесь был бы обвинением: процент от нуля не считается,
 * а не равен нулю.
 */
export function occupancyText(booked: SummaryBooked): string | null {
  if (booked.occupancyPercent === null) return null
  const days = booked.rows.filter((row) => row.workMinutes > 0).length
  return `${String(booked.occupancyPercent)} % · ${String(days)} ${plural(days, "день", "дня", "дней")} со сменами`
}
